import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import MissingFilesNotice from "./MissingFilesNotice";
import { LayoutDashboard, Users, FileText, Settings, LogOut, Menu, X, ChevronRight, CarFront, Shield, Truck, Wrench, ShoppingCart, Moon, Sun, MapPinned, ClipboardCheck, Sparkles, Hammer, PackageSearch, Globe } from "lucide-react";

const NavItem = ({ to, icon: Icon, label, onClick }) => (
  <NavLink to={to} onClick={onClick}
    style={({ isActive }) => ({
      display: "flex", alignItems: "center", gap: "12px", height: "44px", padding: "0 16px", borderRadius: "8px", fontSize: "14px", fontWeight: 500,
      color: isActive ? "#E53935" : "#666666", backgroundColor: isActive ? "#FFEBEE" : "transparent", textDecoration: "none", transition: "all 150ms ease"
    })}>
    {({ isActive }) => (<><Icon size={20} color={isActive ? "#E53935" : "currentColor"} /><span>{label}</span>{isActive && <ChevronRight size={16} style={{ marginLeft: "auto", opacity: 0.5 }} />}</>)}
  </NavLink>
);

const Sidebar = ({ onClose, isMobile, onLogout, user, isVisible = true }) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "include" }); } catch (e) {}
    onLogout?.();
    navigate("/login");
  };
  
  const navItems = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/customers", icon: Users, label: "Zákazníci" },
    { to: "/machines", icon: CarFront, label: "Trenažéry" },
    { to: "/mapa-klientu", icon: MapPinned, label: "Mapa klientů" },
    { to: "/mapy-testy", icon: Globe, label: "Testování map" },
    { to: "/odvozy", icon: ClipboardCheck, label: "Odvozy" },
    { to: "/upravy", icon: Hammer, label: "Úpravy" },
    { to: "/ai", icon: Sparkles, label: "AI asistent" },
    { to: "/orders", icon: ShoppingCart, label: "Objednávky" },
    ...(user?.role === 'admin' ? [{ to: "/zasilky", icon: PackageSearch, label: "Zásilky" }] : []),
    { to: "/suppliers", icon: Truck, label: "Dodavatelé" },
    { to: "/parts", icon: Wrench, label: "Díly" },
    { to: "/templates", icon: FileText, label: "Šablony" },
    { to: "/settings", icon: Settings, label: "Nastavení" }
  ];

  // Přidat Admin panel pro admin/manager
  if (user?.role === 'admin' || user?.role === 'manager') {
    navItems.push({ to: "/admin", icon: Shield, label: "Administrace" });
  }

  const sidebarStyle = isMobile ? {
    width: "240px",
    height: "100vh",
    backgroundColor: "#FFFFFF",
    borderRight: "1px solid #E0E0E0",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    left: 0,
    top: 0,
    zIndex: 100,
    transform: isVisible ? "translateX(0)" : "translateX(-100%)",
    transition: "transform 300ms ease-in-out"
  } : {
    width: "240px",
    height: "100vh",
    backgroundColor: "#FFFFFF",
    borderRight: "1px solid #E0E0E0",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    left: 0,
    top: 0,
    zIndex: 100
  };

  return (
    <aside style={sidebarStyle}>
      <div style={{ height: "80px", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #E0E0E0" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <img src="/logo.png" alt="BasicGame" style={{ height: "70px", width: "auto" }} />
        </div>
        {isMobile && <button onClick={onClose} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "none", backgroundColor: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#999999" }}><X size={20} /></button>}
      </div>
      <nav style={{ flex: 1, padding: "16px", display: "flex", flexDirection: "column", gap: "4px", overflowY: "auto" }}>
        {navItems.map((item) => <NavItem key={item.to} to={item.to} icon={item.icon} label={item.label} onClick={isMobile ? onClose : undefined} />)}
      </nav>
      <div style={{ padding: "16px", borderTop: "1px solid #E0E0E0" }}>
        <button onClick={handleLogout} style={{ display: "flex", alignItems: "center", gap: "12px", width: "100%", height: "44px", padding: "0 16px", borderRadius: "8px", fontSize: "14px", fontWeight: 500, color: "#666666", backgroundColor: "transparent", border: "none", cursor: "pointer" }}>
          <LogOut size={20} /><span>Odhlásit se</span>
        </button>
      </div>
    </aside>
  );
};

function ThemeToggle() {
  const [dark, setDark] = React.useState(() => localStorage.getItem("erp-dark") === "1");
  React.useEffect(() => {
    const el = document.documentElement;
    if (dark) {
      el.style.filter = "invert(0.9) hue-rotate(180deg)";
      el.style.backgroundColor = "#111";
    } else {
      el.style.filter = "";
      el.style.backgroundColor = "";
    }
    localStorage.setItem("erp-dark", dark ? "1" : "0");
  }, [dark]);
  return React.createElement("button", { onClick: () => setDark(!dark), style: { padding: "8px", border: "1px solid #E0E0E0", borderRadius: "8px", background: "#fff", cursor: "pointer", marginRight: "12px", display: "flex", alignItems: "center" } },
    dark ? React.createElement(Sun, { size: 18, color: "#FFA726" }) : React.createElement(Moon, { size: 18, color: "#666" })
  );
};

const Header = ({ onMenuClick, user }) => (
  <header className="erp-header" style={{ height: "64px", backgroundColor: "#FFFFFF", borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", position: "sticky", top: 0, zIndex: 50 }}>
    <ThemeToggle />
    <button onClick={onMenuClick} style={{ width: "40px", height: "40px", borderRadius: "8px", border: "none", backgroundColor: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#666666" }} className="lg-hide"><Menu size={20} /></button>
    <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "8px 16px", borderRadius: "8px", backgroundColor: "#F5F5F5", marginLeft: "auto" }}>
      <div style={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "#E53935", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFFFFF", fontSize: "12px", fontWeight: 600 }}>{user?.username?.charAt(0).toUpperCase() || user?.full_name?.charAt(0).toUpperCase() || "A"}</div>
      <div><p style={{ fontSize: "14px", fontWeight: 600, color: "#333333", margin: 0 }}>{user?.full_name || user?.username || "Uživatel"}</p><p style={{ fontSize: "12px", color: "#666666", margin: 0, textTransform: "capitalize" }}>{user?.role || "user"}</p></div>
    </div>
  </header>
);

const Layout = ({ children, user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  const handleOpenSidebar = () => {
    setSidebarOpen(true);
    // Double rAF zajistí, že DOM je mountnutý předtím než spustíme animaci
    requestAnimationFrame(() => requestAnimationFrame(() => setSidebarVisible(true)));
  };

  const handleCloseSidebar = () => {
    setSidebarVisible(false);
    // Počkej na dokončení slide-out animace (300ms), pak unmount
    setTimeout(() => setSidebarOpen(false), 300);
  };

  // Inject dark mode styles
  React.useEffect(() => {
    const el = document.documentElement;
    if (localStorage.getItem("erp-dark") === "1") {
      el.style.filter = "invert(0.9) hue-rotate(180deg)";
      el.style.backgroundColor = "#111";
    }
  }, []);

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div className="sidebar-desktop"><Sidebar onLogout={onLogout} user={user} /></div>
      {sidebarOpen && (
        <>
          <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 99, opacity: sidebarVisible ? 1 : 0, transition: "opacity 300ms ease" }} onClick={handleCloseSidebar} />
          <Sidebar isVisible={sidebarVisible} onClose={handleCloseSidebar} isMobile onLogout={onLogout} user={user} />
        </>
      )}
      <div style={{ flex: 1, marginLeft: 0, display: "flex", flexDirection: "column" }} className="main-content">
        <Header onMenuClick={handleOpenSidebar} user={user} />
        <main style={{ flex: 1, backgroundColor: "#F5F5F5", minHeight: "calc(100vh - 64px)" }}>{children}</main>
      </div>
      <MissingFilesNotice />
      <style>{`
        @media (min-width: 1024px) { .sidebar-desktop { display: block !important; } .main-content { margin-left: 240px !important; } .lg-hide { display: none !important; } }
        @media (max-width: 1023px) { .sidebar-desktop { display: none !important; } }
      `}</style>
    </div>
  );
};

export default Layout;
